/* TPUSA mega menu (preview 2026-10-17; desktop hover controller 2026-10-18, ported from the Superluxe fix).
   Venue's own hover handling (nested-menu row mouseenter / list mouseleave + menu-aim) is blocked for mouse input and
   replaced by this controller:
   - first open waits OPEN_DELAY (140ms) of hover intent; a quick fly-over opens nothing
   - while a panel is open, moving onto another dropdown item switches INSTANTLY, with no fade-out/fade-in (no blank flash)
   - the nav row, the gaps between items and the band down to the panel are one hover zone, so gaps never close the menu
   - the menu closes CLOSE_DELAY (180ms) after the pointer leaves that zone and the open panel; a plain link with no dropdown
     (Shop All, Gifts, Sale, About Us) counts as leaving. Re-entering the nav or the panel cancels the close.
   - a closed/closing panel can't be hovered or clicked (CSS); the underline shows on the open item only (CSS)
   Touch and pen input keep Venue's behaviour. Keyboard: Enter/Space/ArrowDown open, Esc closes and returns focus.
   aria-expanded is maintained by Venue's header-details-disclosure open()/close(). */
(() => {
  const OPEN_DELAY = 140, CLOSE_DELAY = 180, SLOP = 8;
  let nav = null, ul = null, rows = [], items = [], lastType = 'mouse';
  let openT = null, openFor = null, closeT = null, offT = null;

  const editing = () => document.body && document.body.hasAttribute('header-details-disclosure-edit');
  const navVisible = () => !!(nav && nav.isConnected && ul && ul.getBoundingClientRect().width > 0);
  const active = () => lastType === 'mouse' && navVisible() && !editing();
  const isOpen = (it) => it.details.hasAttribute('open');
  const openItem = () => items.find(isOpen);
  const cancelOpen = () => { clearTimeout(openT); openT = null; openFor = null; };
  const cancelClose = () => { clearTimeout(closeT); closeT = null; };

  const mark = () => {
    const cur = openItem();
    rows.forEach((li) => li.classList.toggle('tp-mm-cur', !!cur && cur.li === li));
    if (cur) { clearTimeout(offT); nav.classList.add('tp-mm-on'); }
    else { clearTimeout(offT); offT = setTimeout(() => nav && nav.classList.remove('tp-mm-on'), 320); }
  };
  const openNow = (it) => {
    cancelOpen(); cancelClose();
    const prev = items.filter((x) => x !== it && isOpen(x));
    if (prev.length) {
      it.details.classList.add('tp-mm-swapin');        // shown at full opacity at once: no blank gap between panels
      prev.forEach((x) => x.el.close());
    }
    if (!isOpen(it)) it.el.open();
    mark();
  };
  const closeAll = () => { cancelOpen(); cancelClose(); items.forEach((x) => { if (isOpen(x)) x.el.close(); }); mark(); };
  const scheduleClose = () => {
    cancelOpen();
    if (!closeT && openItem()) closeT = setTimeout(() => { closeT = null; closeAll(); }, CLOSE_DELAY);
  };

  // Where is the pointer?  'trigger' (a dropdown item in the row), 'zone' (open panel, gaps, band to the panel) or 'out'
  const where = (e) => {
    const t = e.target instanceof Element ? e.target : null;
    const open = openItem();
    if (t && open && open.panel.contains(t)) return { kind: 'zone' };
    const rects = rows.map((li) => li.getBoundingClientRect()).filter((r) => r.width);
    if (!rects.length) return { kind: 'out' };
    const top = Math.min(...rects.map((r) => r.top)), bottom = Math.max(...rects.map((r) => r.bottom));
    const left = Math.min(...rects.map((r) => r.left)) - SLOP, right = Math.max(...rects.map((r) => r.right)) + SLOP;
    const li = t && rows.find((r) => r.contains(t));
    if (li && e.clientY <= bottom + 4) {
      const it = items.find((x) => x.li === li);
      return it ? { kind: 'trigger', it } : { kind: 'out' };   // plain link: counts as leaving
    }
    const x = e.clientX, y = e.clientY;
    if (x < left || x > right || y < top - SLOP) return { kind: 'out' };
    if (y <= bottom + SLOP) return { kind: 'zone' };
    if (open && y <= Math.max(open.panel.getBoundingClientRect().top + 2, bottom + SLOP)) return { kind: 'zone' };
    return { kind: 'out' };
  };
  const keyboardHolds = (open) => {
    const a = document.activeElement;
    return !!(a && open.details.contains(a) && a.matches(':focus-visible'));
  };
  const onMove = (e) => {
    if (e.type === 'pointermove') lastType = e.pointerType || 'mouse';
    if (!items.length || !active()) return;
    const w = where(e);
    const open = openItem();
    if (w.kind === 'trigger') {
      cancelClose();
      if (isOpen(w.it)) { cancelOpen(); return; }
      if (open) { openNow(w.it); return; }                     // a panel is showing: switch immediately
      if (openFor !== w.it) {
        cancelOpen(); openFor = w.it;
        openT = setTimeout(() => { const it = openFor; openT = null; openFor = null; if (it && active()) openNow(it); }, OPEN_DELAY);
      }
    } else if (w.kind === 'zone') {
      cancelClose(); if (openFor && !open) cancelOpen();       // gap/band: keep what's open, don't start opening from below
    } else if (open && !keyboardHolds(open)) {
      scheduleClose();
    } else {
      cancelOpen();
    }
  };
  document.addEventListener('pointerdown', (e) => { lastType = e.pointerType || 'mouse'; }, { passive: true, capture: true });
  document.addEventListener('pointermove', onMove, { passive: true, capture: true });
  document.addEventListener('mouseover', onMove, { passive: true, capture: true });
  // Leaving the page or moving straight into an iframe (chat widget, preview bar) counts as leaving
  const leave = () => { if (active() && openItem()) scheduleClose(); else cancelOpen(); };
  document.documentElement.addEventListener('mouseleave', leave);
  document.addEventListener('mouseout', (e) => { const r = e.relatedTarget; if (!r || r.tagName === 'IFRAME') leave(); }, { passive: true, capture: true });
  window.addEventListener('blur', cancelOpen);

  const init = () => {
    const n = document.querySelector('.js-primary-nav nested-menu');
    if (!n) return;
    if (n !== nav) {
      nav = n; ul = n.querySelector(':scope > ul'); items = [];
      if (!ul) return;
      // Block Venue's mouse hover handlers (row mouseenter -> menu-aim open, list mouseleave -> 300ms close) for mouse input.
      // Capture on the ancestor runs before the listeners on the rows / list. Touch keeps Venue's flow.
      const block = (e) => {
        const t = e.target;
        if (active() && (t === ul || rows.includes(t) || items.some((x) => x.el === t || x.details === t))) e.stopPropagation();
      };
      nav.addEventListener('mouseenter', block, true);
      nav.addEventListener('mouseleave', block, true);
    }
    rows = [...ul.querySelectorAll(':scope > li')];
    rows.forEach((li) => {
      const el = li.querySelector(':scope > header-details-disclosure');
      const details = el && el.querySelector(':scope > details');
      if (!el || !details || items.some((x) => x.el === el)) return;
      const it = { li, el, details, panel: details.querySelector(':scope > .nav__sub') || details.lastElementChild, link: details.querySelector(':scope > summary > a') };
      items.push(it);
      new MutationObserver(() => { if (!isOpen(it)) details.classList.remove('tp-mm-swapin'); mark(); })
        .observe(details, { attributes: true, attributeFilter: ['open'] });
      if (!it.link) return;
      // Mouse click on a closed item opens it now (Venue swallows that click until the panel is open); 2nd click follows the link
      it.link.addEventListener('click', () => { if (active() && !isOpen(it)) openNow(it); });
      // Keyboard: Enter/Space/ArrowDown on a closed item opens its panel (2nd Enter follows the link)
      it.link.addEventListener('keydown', (e) => {
        if (!['Enter', ' ', 'ArrowDown'].includes(e.key)) return;
        if (isOpen(it) && e.key === 'Enter') return;
        e.preventDefault();
        if (!isOpen(it)) openNow(it);
        if (e.key === 'ArrowDown') {
          const first = details.querySelector('.nav__sub a[href]');
          if (first) setTimeout(() => first.focus(), 230);
        }
      });
    });
    items = items.filter((x) => x.el.isConnected);
  };
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = openItem();
    if (!open) return;
    const hadFocus = open.details.contains(document.activeElement);
    closeAll();
    if (hadFocus && open.link) open.link.focus();
  });
  const start = () => { init(); };
  if (customElements.get('nested-menu')) start(); else customElements.whenDefined('nested-menu').then(start);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  document.addEventListener('shopify:section:load', () => { nav = null; init(); });
})();
